<template>
  <div class='add-new-service-provider-user-form'>
    <v-form ref="form">
    <div class='add-new-service-provider-user-details'>
      <span class="header-title part"> {{ $t('add.sp.employee.form.title') }}</span>
      <v-text-field
        class="left-input"
        @change="generalInfoChanged"
        v-model="firstName"
        :rules='[rules.required]'
        :label="$t('registration.client.info.firstname')"
        :placeholder="$t('registration.client.info.firstname.placeholder')"
        dense
        outlined
        required
        autofocus
      />
      <v-text-field
        @change='generalInfoChanged'
        :label="$t('registration.client.info.lastname')"
        :placeholder="$t('registration.client.info.lastname.placeholder')"
        :rules='[rules.required]'
        v-model='lastName'
        dense
        outlined
        required
      />
      <v-text-field
        @change='generalInfoChanged'
        v-model='jobTitle'
        :label="$t('registration.client.info.jobtitle')"
        :placeholder="$t('registration.client.info.jobtitle.placeholder')"
        :rules='[rules.required]'
        dense
        outlined
        required
      />
      <vue-tel-input-vuetify
        mode="international"
        v-model='phoneNumber'
        @input='generalInfoChanged'
        :placeholder="this.$t('registration.client.info.phonenumber.placeholder')"
        :label="$t('registration.client.info.phonenumber')"
        :selectLabel="$t('registration.client.info.phonenumber.country')"
        :default-country='"il"'
        outlined
        dense
        required
      />
      <div class='error' v-if='!phoneNumberValid'> {{ $t('registration.client.info.phone.number.is.not.valid') }} </div>
      <v-text-field
        @change='generalInfoChanged'
        :label="$t('registration.client.info.workemail')"
        :placeholder="$t('registration.client.info.workemail.placeholder')"
        :rules='[rules.required, rules.email, rules.endsWithCompanyDomain]'
        v-model='workEmail'
        dense
        outlined
        required
      />
      <buttons-switcher
        @change='generalInfoChanged'
        v-model="workingDays"
        class='working-days'
        :items="spWorkerWorkingDaysOptions"
        :title="$t('add.sp.employee.form.working.days.title')"
      />
      <div class="time-range d-flex flex-row">
        <div class='working-hours'>
          <label class="label-section-title">
            {{ $t('add.sp.employee.form.working.hours.title') }}
          </label>
          <div class='working-hours-picker'>
            <custom-timepicker
              v-model='startWorkingHour'
              :title="$t('add.sp.employee.form.working.start.hour.title')"
              class="time-picker start-time form-item"
            />
            <custom-timepicker
              v-model='endWorkingHour'
              :title="$t('add.sp.employee.form.working.end.hour.title')"
              class="time-picker end-time form-item"
            />
          </div>
        </div>
      </div>
      <div class='service-provider-data-row'>
        <div class='left-side'>
          <v-text-field
            :rules='[rules.isNumber]'
            v-model='experience'
            @change='generalInfoChanged'
            :label="$t('add.sp.employee.form.experience.title')"
            :placeholder="$t('add.sp.employee.form.rate.per.hour.placeholder')"
            dense
            outlined
            required
          />
        </div>
        <div class='right-side'>
          <v-text-field
            v-model='hourRate'
            :rules='[rules.isNumber]'
            @change='generalInfoChanged'
            :label="$t('add.sp.employee.form.rate.per.hour.title')"
            :placeholder="$t('add.sp.employee.form.rate.per.hour.placeholder')"
            dense
            outlined
            required
          />
        </div>
      </div>
      <div class="additional-comment-section">
        <label class="label-section-title">
          {{ $t('add.sp.employee.form.short.bio.title') }}
        </label>
       <div class="details-data-section">
        <textarea
          v-model='summary'
          @change='generalInfoChanged'
          maxlength="1000"
          :placeholder="$t('add.sp.employee.form.short.bio.placeholder')"
          class="form-control"
        ></textarea>
        </div>
      </div>
        <div class='technical-skills-section'>
          <span class="header-title-second part"> {{ $t('add.sp.employee.form.technical.skills.title') }}</span>
        </div>
        <div class='service-provider-services'>
          <div class="company-services-sub-title">
            {{ $t('add.sp.employee.form.services.title') }}
          </div>
          <v-list
            flat
            class="services-list"
          >
            <v-list-group
              v-for="service in topLevelServicesWithSubs"
              :key="service"
              :append-icon="''"
            >
              <template v-slot:activator>
                <v-list-item-icon>
                  <v-icon color="#E7E9EC" small>add</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
                </v-list-item-content>
              </template>

              <v-list-item
                v-for="subService in formattedServicesOptions[service]"
                :key="subService"
              >
                <template>
                  <v-list-item-action>
                    <custom-checkbox
                      color="cold-marine"
                      size="s"
                      :value="flatServicesOptionsObject[subService]"
                      @input="onCheck(subService)"
                    ></custom-checkbox>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title v-text="$t(`${subService}`)"></v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-list-group>
            <v-list-item
              v-for="service in topLevelServicesWithoutSubs"
              :key="service"
            >
              <template>
                <v-list-item-action>
                  <custom-checkbox
                    :value="flatServicesOptionsObject[service]"
                    color="cold-marine"
                    @input="onCheck(service)"
                    size="s"
                  ></custom-checkbox>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
          </v-list>
        </div>
        <div class='service-provider-certification'>
          <div class="company-certification-sub-title">
            {{ $t('add.sp.employee.form.certification.title') }}
          </div>
          <div class='certification-options-section'>
            <div v-for="(certificate) in spFreelancerCertificates" :key="certificate.code">
              <custom-checkbox
                :title="$t(`${certificate.code}`)"
                :value="checkBoxValues[certificate.code]"
                class="feature-checkbox"
                color="cold-marine"
                @input="onSelect(certificate)"
              />
              <CertificateScan
                v-if="checkBoxValues[certificate.code]"
                :certificateCode="certificate.code"
                :is-manual="false"
                @changed="certificationsChanged"
              />
            </div>
          </div>
        </div>
        <div class='ip-section'>
          <div class="company-ip-sub-title">
            {{ $t('add.sp.employee.form.ip.section.title') }}
          </div>
          <div class="text-field-section">
            <v-text-field
              :rules='[rules.hostWithRange]'
              @change='generalInfoChanged'
              type="text"
              v-model="ipAddress"
              class="login"
              :label="$t('registration.sp.freelancer.company.certification.white.ip.label')"
              :placeholder="$t('registration.sp.freelancer.company.certification.white.ip.placeholder')"
              dense
              outlined
              required
            />
          </div>
        </div>
      </div>
    </v-form>
      <div class='add-new-service-provider-user-avatar'>
        <avatar-upload  class="avatar" :file-url="avatarUrl" @changed='avatarChanged'/>
      </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import AvatarUpload from '@/components/common/AvatarUpload.vue';
import {
  LOAD_CERTIFICATES,
  LOAD_SERVICES,
} from '@/store/spFreelancerRegistration/actionTypes';
import ButtonsSwitcher from '@/components/common/ButtonsSwitcher.vue';
import CustomTimepicker from '@/components/datepicker/CustomTimepicker.vue';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import CertificateScan from '@/components/spFreelancerRegistration/CertificateScan.vue';
import { rules } from '@/utils/validationUtils';
import { spWorkerWorkingDays } from '@/constants/registration';

// eslint-disable-next-line no-unused-vars
const getTime = hour => moment()
  .hour(hour)
  .minute(0)
  .startOf('hour')
  .format('HH:mm');
const startingBusinessDay = getTime(9);
const endingBusinessDay = getTime(18);

export default {
  name: 'AddNewServiceProviderUserForm',
  components: {
    AvatarUpload,
    ButtonsSwitcher,
    CustomTimepicker,
    CustomCheckbox,
    CertificateScan,
  },
  props: {
    generalInfo: {
      type: Object,
      required: true,
    },
    servicesChosenInfo: {
      type: Array,
      required: true,
    },
    certificationChosenInfo: {
      type: Array,
      required: true,
    },
    avatar: {
      required: true,
    },
  },
  watch: {
    phoneNumber() {
      if (this.rules.phone(this.phoneNumber) === true) {
        this.phoneNumberValid = true;
      } else {
        this.phoneNumberValid = false;
      }
    },
    certificatesListChosen() {
      this.certificationsChanged();
    },
    startWorkingHour() {
      this.generalInfoChanged();
    },
    endWorkingHour() {
      this.generalInfoChanged();
    },
    servicesChosen() {
      this.servicesChanged();
    },
  },
  created() {
    this.$store.dispatch(LOAD_SERVICES);
    this.$store.dispatch(LOAD_CERTIFICATES);
  },
  data() {
    return {
      phoneNumberValid: false,
      valid: this.generalInfo.valid || false,
      avatarUrl: null,
      ipAddress: this.generalInfo.ipAddress || '',
      servicesChosen: [],
      certificatesListChosen: [],
      selectedFile: {},
      workEmail: this.generalInfo.workEmail || '',
      firstName: this.generalInfo.firstName || '',
      lastName: this.generalInfo.lastName || '',
      hourRate: this.generalInfo.hourRate || 0,
      phoneNumber: this.generalInfo.phoneNumber || '',
      jobTitle: this.generalInfo.jobTitle || '',
      experience: this.generalInfo.experience || 0,
      summary: this.generalInfo.summary || '',
      workingDays: this.generalInfo.workingDays || [],
      startWorkingHour: this.generalInfo.startWorkingHour || startingBusinessDay,
      endWorkingHour: this.generalInfo.endWorkingHour || endingBusinessDay,
    };
  },
  methods: {
    async generalInfoChanged() {
      this.$nextTick(async () => {
        this.valid = this.$refs.form.inputs.every(input => input.valid) && this.phoneNumberValid;
        const generalInfo = {
          valid: this.valid,
          firstName: this.firstName,
          lastName: this.lastName,
          jobTitle: this.jobTitle,
          experience: this.experience,
          workingDays: this.workingDays,
          startWorkingHour: this.startWorkingHour,
          endWorkingHour: this.endWorkingHour,
          workEmail: this.workEmail,
          summary: this.summary,
          hourRate: this.hourRate,
          phoneNumber: this.phoneNumber,
          ipAddress: this.ipAddress,
        };
        await this.$emit('generalInfoChanged', generalInfo);
      });
    },
    async avatarChanged(file) {
      this.avatarUrl = file;
      this.$emit('avatarChanged', this.avatarUrl);
    },
    async certificationsChanged(value) {
      this.selectedFile[value?.code] = value?.file;
      await this.$emit('certificationsChanged', this.allCertificatesInfo);
    },
    async servicesChanged() {
      await this.$emit('servicesChanged', this.servicesChosen);
    },
    isValid(value) {
      return rules.hostWithRange(value);
    },
    onCheck(service) {
      if (!this.servicesChosen.includes(service)) {
        this.servicesChosen.push(service);
      } else {
        const index = this.servicesChosen.indexOf(service);
        this.servicesChosen.splice(index, 1);
      }
    },
    mapToObj(map) {
      const obj = {};
      map.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    },
    onSelect(certificate) {
      if (!this.certificatesListChosen.includes(certificate.code)) {
        this.certificatesListChosen.push(certificate.code);
      } else {
        const index = this.spFreelancerCertificates.findIndex(element => element === certificate.code);
        this.certificatesListChosen.splice(index, 1);
      }
    },
  },
  computed: {
    spWorkerWorkingDaysOptions() {
      return spWorkerWorkingDays;
    },
    ...mapGetters([
      'currentSPEmployee',
      'spFreelancerServices',
      'spFreelancerCertificates',
      'profile',
      'spWorkerServicesChosen',
      'spWorkerCertificationsChosen',
      'spWorkerGeneralInfo',
      'spWorkerAvatar',
    ]),
    rules() {
      return {
        ...rules,
        // eslint-disable-next-line no-restricted-globals
        isNumber: v => (!isNaN(v) && v >= 0) || this.$t('only.numbers'),
        phone: v => rules.phone(v) || this.$t('registration.client.info.phone.number.is.not.valid'),
        email: v => rules.email(v.toLowerCase()) || this.$t('registration.client.info.email.is.not.valid'),
        required: v => rules.required(v) || this.$t('required'),
        endsWithCompanyDomain: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          (!!v && v.toLowerCase().endsWith(this.domain.toLowerCase())) ||
          this.$t('registration.client.info.email.should.be.in.company.domain'),
      };
    },
    domain() {
      return this.profile.company.domain;
    },
    fileUrl() {
      return this.currentSPEmployee?.avatarURL;
    },
    topLevelServicesWithoutSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => !this.formattedServicesOptions[service].length);
    },
    topLevelServicesWithSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => this.formattedServicesOptions[service].length);
    },
    formattedServicesOptions() {
      const servicesMap = new Map();
      this.spFreelancerServices.forEach(service => {
        if (service.shortName.includes('.sub.')) {
          const topLevelService = service.shortName.split('.sub.')[0];
          if (!servicesMap.has(topLevelService)) {
            servicesMap.set(topLevelService, [service.shortName]);
          } else {
            const currentSubServices = servicesMap.get(topLevelService);
            currentSubServices.push(service.shortName);
            servicesMap.set(topLevelService, currentSubServices);
          }
        } else if (!servicesMap.has(service.shortName)) {
          servicesMap.set(service.shortName, []);
        }
      });
      return this.mapToObj(servicesMap);
    },
    flatServicesOptionsObject() {
      const flatServicesObject = {};
      this.topLevelServicesWithoutSubs.forEach(
        service => {
          flatServicesObject[service] = this.servicesChosen.includes(service);
        },
      );
      this.topLevelServicesWithSubs.forEach(
        service => {
          this.formattedServicesOptions[service].forEach(
            subService => {
              flatServicesObject[subService] = this.servicesChosen.includes(subService);
            },
          );
        },
      );
      return flatServicesObject;
    },
    checkBoxValues() {
      const certificatesValueObject = {};
      this.spFreelancerCertificates.forEach(certificate => {
        certificatesValueObject[certificate.code] = this.certificatesListChosen.includes(certificate.code);
      });
      return certificatesValueObject;
    },
    allCertificatesInfo() {
      const defaultCertificatesWithScanData = [];
      const keys = Object.keys(this.selectedFile);
      this.certificatesListChosen.forEach(certificate => {
        if (keys.includes(certificate)) {
          defaultCertificatesWithScanData.push({
            code: certificate,
            file: this.selectedFile[certificate],
          });
        } else {
          defaultCertificatesWithScanData.push({
            code: certificate,
            file: null,
          });
        }
      });
      return defaultCertificatesWithScanData;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.add-new-service-provider-user-form {
  display: flex;
  padding-left: rem(104);
  padding-top: rem(60);
  padding-right: rem(104);
  .add-new-service-provider-user-details {
    flex: 32%;
    margin-right: 8%;
  }
  .error {
    background: transparent;
    text-transform: capitalize;
    color: var(--severity-critical);
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: rem(1.12);
    line-height: $space-4;
    margin-bottom: $space-4;
  }
  .add-new-service-provider-user-avatar {
    flex: 1;
  }
  .technical-skills-section {
    margin-top: rem(60);
    .header-title-second {
      height: rem(24);
      width: rem(105);
      color: var(--text-primary);
      font-family: $font-family-medium;
      font-size: $font-size-xl;
      font-weight: 500;
      letter-spacing: 0;
      line-height: rem(24);
    }
  }
  .service-provider-services {
    width: fit-content;
    color: var(--text-primary);
    font-family: $font-family-regular;
    .company-services-sub-title {
      height: $font-size-xs;
      width: rem(103);
      color: var(--text-secondary);
      font-size: $font-size-xs;
      letter-spacing: rem(0.8);
      line-height: $font-size-xs;
      text-transform: uppercase;
      margin-bottom: $space-3;
    }
  }
  .save-new-service-provider-data {
    padding-top: 10%;
    padding-bottom: 10%;
    .save-btn {
      height: $btn-height-md;
      border-radius: $radius-sm;
      background-color: var(--brand-primary);
      box-shadow: 0 $border-width-thin $border-width-thin 0 #dbdbdb, 0 rem(2) $border-width-thin rem(-1) #e0e0e0, 0 $border-width-thin rem(3) 0 #cccccc;
      color: var(--text-inverse);
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: $space-4;
      text-align: center;
      margin-left: $space-2;
    }
  }

  .service-provider-certification {
    width: fit-content;
    max-width: rem(400);
    margin-top: $space-6;
    color: var(--text-primary);
    font-family: $font-family-regular;
    .certification-options-section {
      height: rem(311);
      overflow-y: scroll;
      overflow-x: hidden;
      margin-bottom: $space-10;
    }
    .company-certification-sub-title {
      height: $font-size-xs;
      width: rem(103);
      color: var(--text-secondary);
      font-size: $font-size-xs;
      letter-spacing: rem(0.8);
      line-height: $font-size-xs;
      text-transform: uppercase;
      margin-bottom: $space-3;
    }
    .file-name {
      height: $space-4;
      color: var(--text-secondary);
      font-family: $font-family-medium;
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: $space-4;
      text-align: center;
    }
  }
  .ip-section {
    width: fit-content;
    min-width: rem(365);
    .company-ip-sub-title {
      height: rem(18);
      color: var(--text-primary);
      font-family: $font-family-regular;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
    }
    .add-ip-form {
      margin-bottom: $space-8;
      .ips-inputs {
        display: flex;
        align-items: center;
        .text-field-section{
          flex:0 0 rem(365);
        }

        .add-new-ip-section {
          flex: 0 0 rem(105);
          height: $space-4;
          color: var(--text-muted);
          font-family: $font-family-regular;
          font-size: $font-size-sm;
          font-weight: 500;
          letter-spacing: rem(1.07);
          line-height: $space-4;
          text-align: center;
          .plus {
            font-size: $font-size-md;
          }
          .add-btn {
            width: rem(86);
            color: var(--text-inverse);
          }
        }
      }
    }
  }
  .header-title {
    height: rem(24);
    width: fit-content;
    color: var(--text-primary);
    font-family: $font-family-medium;
    font-size: $font-size-xl;
    font-weight: 500;
    letter-spacing: 0;
    line-height: rem(24);
  }
  .part {
    flex: 1;
  }
  .details-data-section {
    border-radius: $radius-sm;
    background-color: var(--bg-primary);
    box-shadow: 0 0 $space-2 0 rgba(12, 18, 62, 0.08);
    margin-top: $space-6;
    .textarea {
      width: 100%;
      height: 100%;
      resize: none;
      padding: $space-2 $space-2 $space-2 $space-3;

      &:focus {
        outline: none;
        border: $border-width-thin solid var(--text-secondary);
        box-shadow: 0 0 $space-2 #719ece;
      }
    }
  }

  .service-provider-data-row {
    display: flex;
    padding-top: $space-6;
    .left-side {
      flex: 1;
      margin-right: 10%;
    }
    .right-side {
      flex: 1;
    }
  }
  .short-bio-text-field {
    .v-text-field {
      height: rem(141);
    }
  }
  .label-section-title {
    height: $font-size-xs;
    width: rem(103);
    color: var(--text-secondary);
    font-size: $font-size-xs;
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-3;
  }
  .additional-comment-section {
    margin-top: $space-6;
      textarea {
      width: 100%;
      height: 100%;
      resize: none;
      padding: $space-2 $space-2 $space-2 $space-3;
      &:focus {
        outline: none;
        border: $border-width-thin solid var(--text-secondary);
        box-shadow: 0 0 $space-2 #719ece;
      }
    }
  }
  .working-days {
    padding-top: $space-6;
  }
  .time-range {
    padding-top: $space-6;
    .working-hours {
      display: flex;
      flex-direction: column;
      .label-section-title {
        height: $font-size-xs;
        color: var(--text-secondary);
        font-size: $font-size-xs;
        letter-spacing: rem(0.8);
        line-height: $font-size-xs;
        text-transform: uppercase;
        margin-bottom: $space-3;
      }
      .working-hours-picker {
        display: flex;
        flex-direction: row;
        .start-time {
          margin-right: $space-2;
        }
        .end-time {
          margin-right: $space-4;
          margin-top: auto;
        }
        .time-picker {
          flex: 1;
        }
        flex: auto;
      }
    }
  }
}
</style>
